import axios, { AxiosInstance } from "axios";
import { PrimitiveAtom, useAtom } from "jotai";
import { createApiInstance } from "src/lib/api";

type ApiOptions = {
	baseUrl: string;
	with?: string[];
	query?: {
		[key: string]: any;
	};
};

type UpdateOptions = {
	updateList?: boolean;
};

type QueryProps = {
	[key: string]: any;
};

export type Pagination = {
	page: number;
	last_page: number;
	setPage: (page: number) => Promise<any[]>;
};

export type ApiFilter = {
	[key: string]: any;
};

export type ApiStatus =
	| "prefetch"
	| "idle"
	| "loading"
	| "updating"
	| "creating"
	| "deleting"
	| "error";

export type ApiActions = {
	set: (data: any) => void;
	get: (id?: string | number, query?: QueryProps) => Promise<void>;
	list: (query?: QueryProps, options?: ListOptions) => Promise<void>;
	update: (params?: any, updateOptons?: UpdateOptions) => Promise<void>;
	create: (data?: any) => Promise<any>;
	upsert: (data?: any) => Promise<any>;
	delete: (id: string | number) => Promise<any>;
};

export type ApiState = {
	data: any;
	list: any[];
	custom?: any;
	status: ApiStatus;
	filter: ApiFilter;
	pagination: {
		page: number;
		last_page: number;
	};
};

type ListOptions = {
	disableLoading?: boolean;
};

export const emptyApiState = {
	data: {},
	list: [],
	custom: {},
	status: "idle",
	filter: {},
	pagination: {
		page: 0,
		last_page: 0,
	},
};

function useApi(
	atom: PrimitiveAtom<ApiState>,
	options: ApiOptions
): {
	state: ApiState;
	api: AxiosInstance;
	actions: ApiActions;
	pagination: Pagination;
} {
	const [state, setState] = useAtom<ApiState, any, any>(atom);

	const getQueryParams = (query?: QueryProps) => {
		return {
			...(options?.with ? { with: options.with } : {}),
			...(options?.query || {}),
			...(state?.filter || {}),
			...(query || {}),
		};
	};

	const handleError = () => {
		setState((prev: any) => ({
			...(prev || {}),
			status: "idle",
		}));
	};

	const api = createApiInstance({
		baseURL: `${axios.defaults.baseURL}${options.baseUrl}`,
	});

	//List a collection of resources
	const list = async (query?: QueryProps, listOptions?: ListOptions) => {
		if (state.status === "loading") {
			return;
		}
		const params = {
			...getQueryParams(query),
		};
		try {
			if (!listOptions?.disableLoading) {
				setState((prev: any) => ({
					...(prev || {}),
					status: "loading",
					filter: params,
				}));
			}
			const { data } = await axios.get(`${options.baseUrl}`, {
				params,
			});
			setState((prev: any) => ({
				...(prev || {}),
				status: "idle",
				list: data?.data || [],
				filter: params,
				pagination: {
					page: data?.meta?.current_page,
					last_page: data?.meta?.last_page,
				},
			}));
			return data?.data || [];
		} catch (error) {
			handleError();
			return [];
		}
	};

	//List a single collection
	const get = async (id?: string | number, query?: QueryProps) => {
		try {
			setState((prev: any) => ({
				...(prev || {}),
				status: "loading",
			}));
			const { data } = await axios.get(
				id ? `${options.baseUrl}/${id}` : `${options.baseUrl}`,
				{
					params: getQueryParams(query),
				}
			);
			setState((prev: any) => ({
				...(prev || {}),
				status: "idle",
				data: data?.data || {},
			}));
			return data?.data || {};
		} catch (error) {
			handleError();
			throw error;
		}
	};

	//Update the selected resource
	const update = async (params: any, updateOptons?: UpdateOptions) => {
		const id = (params || {})?.id || state?.data?.id;
		if (!id) {
			return;
		}
		setState((prev: any) => ({
			...(prev || {}),
			status: "updating",
		}));
		try {
			const { data } = await axios.patch(
				`${options.baseUrl}/${id}`,
				params,
				{
					params: getQueryParams(),
				}
			);
			setState((prev: any) => {
				if (updateOptons?.updateList) {
					return {
						...(prev || {}),
						status: "idle",
						data: data?.data || {},
						list: prev.list.map((item: any) => {
							if (item?.id === data?.data?.id) {
								return data.data;
							}
							return item;
						}),
					};
				}
				return {
					...(prev || {}),
					status: "idle",
					data: data?.data || {},
				};
			});

			return data?.data || {};
		} catch (error) {
			handleError();
			throw error;
		}
	};

	//Create and set the tenant
	const create = async (params: any) => {
		setState((prev: any) => ({
			...(prev || {}),
			status: "creating",
		}));
		try {
			const { data } = await axios.post(`${options.baseUrl}`, params, {
				params: getQueryParams(),
			});
			setState((prev: any) => ({
				...(prev || {}),
				status: "idle",
				// data: data?.data || {},
			}));

			return data?.data || {};
		} catch (error: any) {
			handleError();
			throw error?.response?.data;
		}
	};

	//Create and set the tenant
	const upsert = async (params: any) => {
		setState((prev: any) => ({
			...(prev || {}),
			status: "creating",
		}));
		try {
			const { data } = await axios.post(`${options.baseUrl}`, params, {
				params: getQueryParams(),
			});
			setState((prev: any) => ({
				...(prev || {}),
				status: "idle",
				data: data?.data || {},
			}));

			return data?.data || {};
		} catch (error) {
			handleError();
			return {};
		}
	};

	const remove = async (id: string | number) => {
		try {
			setState((prev: any) => ({
				...(prev || {}),
				status: "deleting",
			}));
			const { data } = await axios.delete(`${options.baseUrl}/${id}`, {
				params: getQueryParams(),
			});
			setState((prev: any) => ({
				...(prev || {}),
				status: "idle",
				data: data?.data || {},
				list: (prev?.list || []).filter(
					(item: any) => item.id !== id && item.hid !== id
				),
			}));
		} catch (error) {
			handleError();
		}
	};

	return {
		state,
		api,
		pagination: {
			page: state?.pagination?.page,
			last_page: state?.pagination?.last_page,
			setPage: (page) => list({ page }),
		},
		actions: {
			set: setState,
			get,
			list,
			update,
			create,
			upsert,
			delete: remove,
		},
	};
}

export default useApi;
